import Cookies from 'js-cookie';
import { getDeviceSource, type DeviceSource } from './device';

export interface LiteUserInfo {
  id: number;
  full_name: string;
  profile_picture: string;
  phone_number: string;
  member_type: number;
  shopName?: string;
  hasPassword?: boolean;
}

export interface AuthTicket {
  t?: number;
  k?: string;
}

export interface SessionCookie {
  id: string;
  startedAt: number;
}

export interface CookiesData {
  userInfo?: LiteUserInfo;
  tgtTimestamp?: number | string;
  tgtTicket?: string;
  ecountry: string;
  source: DeviceSource;
  device_uuid: string;
  NEXT_LOCALE: string;
  VIEWED_LISTING_COOKIE: string;
  audience: string;
  cityId: string;
  alert_message: string;
  show_success_delete: string;
  popupAppearanceDays?: string;
  saveUserProfileData?: string;
  clickedOnMyListings?: string;
  session?: SessionCookie;
}

export interface CookieOptions {
  expires?: string | number | Date;
  path?: string;
  domain?: string;
}

const parseJson = <T>(value: string | undefined): T | undefined => {
  if (!value) return undefined;

  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
};

const getCookieDomain = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  if (window.location.hostname.includes('localhost')) return window.location.hostname;

  const domainParts = window.location.hostname.split('.').reverse();
  const topLevelDomain = domainParts[0];
  const domain = domainParts[1];

  return topLevelDomain && domain ? `.${domain}.${topLevelDomain}` : undefined;
};

export const defaultOptions: CookieOptions = {
  expires: process.env.REACT_APP_DEFAULT_COOKIES_EXPIRED_IN_MINUTES,
  path: '/',
  domain: getCookieDomain(),
};

export const deleteCookie = (name: string, options: CookieOptions = defaultOptions): void => {
  const { path = '/', domain = '' } = options;

  let cookie = `${name}=; Max-Age=0`;

  if (path) cookie += `;path=${path}`;
  if (domain) cookie += `;domain=${domain}`;

  document.cookie = cookie;
};

export const setCookie = (name: string, value: string, options: CookieOptions): void => {
  let { expires = '', path = '/', domain = '' } = options;

  if (typeof expires === 'number') {
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + expires * 60 * 1000);
    expires = expirationDate;
  }

  let cookie = `${name}=${value}`;

  if (expires instanceof Date) cookie += `;expires=${expires.toUTCString()}`;
  if (path) cookie += `;path=${path}`;
  if (domain) cookie += `;domain=${domain}`;

  document.cookie = cookie;
};

// Called during render by the header, sidebar and most home-page widgets, and each
// build reads ~15 cookies and runs 3 JSON.parse calls. Keying the cache on the raw
// cookie string invalidates it automatically, and returning a stable reference lets
// callers use the result as a hook dependency.
let cached: { raw: string; value: CookiesData } | null = null;

export const getCookies = (): CookiesData => {
  const raw = document.cookie;

  if (cached?.raw === raw) {
    return cached.value;
  }

  const auth = parseJson<AuthTicket>(Cookies.get('auth'));
  const source = getDeviceSource();

  const value: CookiesData = {
    userInfo: parseJson<LiteUserInfo>(Cookies.get('userInfo')),
    tgtTimestamp: auth?.t ?? Cookies.get('tgt_timestamp'),
    tgtTicket: auth?.k ?? Cookies.get('tgt_ticket'),
    ecountry: Cookies.get('ecountry') ?? '',
    source,
    device_uuid: Cookies.get('device_uuid') ?? '',
    NEXT_LOCALE: Cookies.get('NEXT_LOCALE') === 'ku' ? 'ar' : (Cookies.get('NEXT_LOCALE') ?? 'ar'),
    VIEWED_LISTING_COOKIE: Cookies.get('recentlyViewed') ?? 'recentlyViewed',
    audience: source,
    cityId: Cookies.get('cityId') ?? '',
    alert_message: Cookies.get('alert_message') ?? '',
    show_success_delete: Cookies.get('show_success_delete') ?? '',
    saveUserProfileData: Cookies.get('saveUserProfileData') ?? '',
    clickedOnMyListings: Cookies.get('clickedOnMyListings'),
    popupAppearanceDays: Cookies.get('popupAppearanceDays') ?? '[]',
    session: parseJson<SessionCookie>(Cookies.get('session')),
  };

  cached = { raw, value };

  return value;
};
